import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getPageLinkByNodeId } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

export interface ServiceGuideLinkFieldValue {
    type: "InfoLink";
    value: string;
}

interface ServiceCardModel {
    fields: {
        image: string;
        title: string;
        description: string;
        link: ServiceGuideLinkFieldValue;
        linkTitle: string;
    };
}

const enum fields {
    serviceCards = "serviceCards",
    title = "title",
    description = "description",
    link = "link",
    linkTitle = "linkTitle",
    serviceCardTitle = "serviceCardTitle",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.serviceCards]: ServiceCardModel[];
        serviceCardTitle: string;
        description?: string;
    };
}

export interface MappedLink {
    title?: string;
    url?: string;
    link?: ServiceGuideLinkFieldValue;
    linkTitle?: string;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const {
        fields: { serviceCards },
    } = ownProps;
    const mappedServiceCards = [];
    for (const card of serviceCards) {
        let mappedLink: MappedLink | undefined;
        const readonlyPageLink = getPageLinkByNodeId(state, card.fields.link.value) || undefined;
        const linkData = readonlyPageLink || undefined;
        if (linkData) {
            mappedLink = { ...card.fields };
            mappedLink.url = linkData.url;
        }
        mappedServiceCards.push(mappedLink);
    }
    return {
        mappedServiceCards,
    };
};
export interface ServiceGuideStyles {
    container?: GridContainerProps;
    childContainer?: GridContainerProps;
    serviceCard?: GridItemProps;
    customFlyTitle?: TypographyPresentationProps;
    servicecardWrapper?: InjectableCss;
    serviceCardTitle?: TypographyPresentationProps;
    customDescriptionText?: TypographyPresentationProps;
    buttonStyle?: LinkPresentationProps;
}

export const serviceGuideStyles: ServiceGuideStyles = {
    serviceCardTitle: {
        variant: "h2",
        css: css`
            text-align: center;
            font-size: 2rem;
            margin: 30px 0px;
            color: ${getColor("primary.main")};
        `,
    },
    container: {
        css: css`
            background-color: #F8F8F8;
            margin: 30px 0px;
        `,
    },
    childContainer: {
        css: css`
            padding: 15px;
        `,
    },
    servicecardWrapper: {
        css: css`
           
        `,
    },
    customDescriptionText: {
        variant: "h2",
        css: css`
            color: #2D3435;
            line-height: 34px;
            font-size: 1.5rem;
            font-weight: 700;
            font-family: OpenSans;
            margin-bottom: 1rem;
            display: block;
        `,
    },
    buttonStyle: {
        css: css`
            span {
                background-color: transparent !important;
                color: ${getColor("primary.main")} !important;
                font-size: 14px;
                font-weight: bold;
                line-height: normal;
                padding: 10px 0px;
                &:hover {
                    text-decoration: underline;
                }
            }
        `,
    },
};

const styles = serviceGuideStyles;

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const ServiceCards: React.FC<Props> = ({ id, fields, mappedServiceCards }) => {
    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.childContainer}>
                <Typography {...styles.customDescriptionText}>
                        {fields?.description}
                </Typography>
                    {mappedServiceCards?.length > 0 ? mappedServiceCards?.map((card, index) => {
                        return (
                            <StyledWrapper key={index} {...styles.servicecardWrapper}>
                                <Link {...styles.buttonStyle} href={card?.url}>
                                    {translate(card?.linkTitle || "")}
                                </Link>
                            </StyledWrapper>
                        )
                    }) : null }
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(ServiceCards),
    definition: {
        group: "Basic",
        fieldDefinitions: [
            {
                name: fields.serviceCards,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.title;
                },
                defaultValue: [],
                fieldType: "Translatable",

                sortOrder: 2,
                fieldDefinitions: [
                    {
                        name: fields.title,
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        sortOrder: 0,
                    },
                    {
                        name: fields.linkTitle,
                        displayName: "Link Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        sortOrder: 1,
                    },
                    {
                        name: fields.link,
                        displayName: "Link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                        sortOrder: 2,
                    }
                ],
            },
            {
                name: fields.description,
                displayName: "Service Guide Title",
                editorTemplate: "TextField",
                defaultValue: "",
                sortOrder: 0,
                fieldType: "Translatable",
            },
        ],
    },
};

export default widgetModule;